import { useCallback, useMemo, useState } from 'react';
import useDarkTheme from './use-dark-theme';
import useDefaultTheme from './use-default-theme';
import useGlassTheme from './use-glass-theme';
import useShadcnTheme from './use-shadcn-theme';
import useLarkTheme from './use-lark-theme';
import useSereneTheme from './use-serene-theme';
import useBlossomTheme from './use-blossom-theme';

export const THEME_STORAGE_KEY = 'kip.theme';

export const THEME_OPTIONS = [
  { label: '经典主题', value: 'default' },
  { label: '暗黑主题', value: 'dark' },
  { label: '玻璃主题', value: 'glass' },
  { label: '优雅主题', value: 'shadcn' },
  { label: '知识主题', value: 'lark' },
  { label: '静谧主题', value: 'serene' },
  { label: '花神主题', value: 'blossom' },
];

const THEME_VALUES = new Set(THEME_OPTIONS.map((option) => option.value));

export const normalizeThemeName = (themeName) => {
  return THEME_VALUES.has(themeName) ? themeName : 'lark';
};

export const readStoredThemeName = () => {
  try {
    return normalizeThemeName(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return normalizeThemeName();
  }
};

export const writeStoredThemeName = (themeName) => {
  const nextThemeName = normalizeThemeName(themeName);
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, nextThemeName);
  } catch {
    // localStorage may be unavailable in private or restricted browser contexts.
  }
  return nextThemeName;
};

const useTheme = () => {
  const defaultTheme = useDefaultTheme();
  const darkTheme = useDarkTheme();
  const glassTheme = useGlassTheme();
  const shadcnTheme = useShadcnTheme();
  const larkTheme = useLarkTheme();
  const sereneTheme = useSereneTheme();
  const blossomTheme = useBlossomTheme();
  const [themeName, setThemeNameState] = useState(() => readStoredThemeName());

  const themeConfigs = useMemo(
    () => ({
      default: defaultTheme,
      dark: darkTheme,
      glass: glassTheme,
      shadcn: shadcnTheme,
      lark: larkTheme,
      serene: sereneTheme,
      blossom: blossomTheme,
    }),
    [darkTheme, defaultTheme, glassTheme, shadcnTheme, larkTheme, sereneTheme, blossomTheme],
  );

  const setThemeName = useCallback((nextThemeName) => {
    setThemeNameState(writeStoredThemeName(nextThemeName));
  }, []);

  return useMemo(
    () => ({
      setThemeName,
      themeConfig: themeConfigs[themeName] || themeConfigs.lark,
      themeName,
    }),
    [setThemeName, themeConfigs, themeName],
  );
};

export default useTheme;
